import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import axios from 'axios';
import { 
  Truck, Battery, Car, Key, Wrench, Shield, ChevronRight, Loader2, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceIcons = {
  tow: Truck,
  jump_start: Battery,
  flat_tire: Car,
  lockout: Key,
  repair: Wrench,
  diagnostic: Shield
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-blue-100 text-blue-800',
  en_route: 'bg-indigo-100 text-indigo-800',
  arrived: 'bg-purple-100 text-purple-800',
  in_progress: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

export default function History() {
  const { getAuthHeaders } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/services`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = requests.filter(req => {
    if (filter === 'all') return true;
    if (filter === 'active') return !['completed', 'cancelled'].includes(req.status);
    if (filter === 'completed') return req.status === 'completed';
    if (filter === 'cancelled') return req.status === 'cancelled';
    return true;
  });

  const groupedByDate = filteredRequests.reduce((groups, req) => {
    const date = new Date(req.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    if (!groups[date]) groups[date] = [];
    groups[date].push(req);
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50" data-testid="history-page">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 font-['Chivo']">Service History</h1>
            <p className="text-gray-600 mt-1">View all your past and current service requests</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'active', label: 'Active' },
            { key: 'completed', label: 'Completed' },
            { key: 'cancelled', label: 'Cancelled' }
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                filter === f.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-300 hover:border-blue-300'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
          </div>
        ) : filteredRequests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Service History</h3>
              <p className="text-gray-600">
                {filter === 'all' 
                  ? "You haven't made any service requests yet."
                  : `No ${filter} requests found.`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedByDate).map(([date, reqs]) => (
              <div key={date}>
                <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {date}
                </h3>
                <div className="space-y-3">
                  {reqs.map((request) => {
                    const ServiceIcon = serviceIcons[request.service_type] || Car;
                    return (
                      <Link
                        key={request.request_id}
                        to={`/customer/tracking/${request.request_id}`}
                        className="block"
                        data-testid={`history-item-${request.request_id}`}
                      >
                        <Card className="hover:shadow-md transition-shadow">
                          <CardContent className="py-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                  <ServiceIcon className="w-6 h-6 text-gray-600" />
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900 capitalize">
                                    {request.service_type.replace('_', ' ')}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {request.vehicle_year} {request.vehicle_make} {request.vehicle_model}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(request.created_at).toLocaleTimeString([], { 
                                      hour: '2-digit', 
                                      minute: '2-digit' 
                                    })}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusColors[request.status]}`}>
                                    {request.status.replace('_', ' ').toUpperCase()}
                                  </span>
                                  <p className="text-lg font-bold text-gray-900 mt-1">
                                    ${request.final_price || request.estimated_price}
                                  </p>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
